<template>
  <div class="container sitemap">
    <h1 class="h2">
      Sitemap
    </h1>
    <div v-for="(section, idx) in sections" :key="idx">
      <h2 class="h3 learn title-link">
        <SitemapLink :data="section" />
      </h2>
      <div v-for="(subSection, idx2) in section.subSections" :key="idx2" class="section">
        <h3 class="h5 title-link">
          <SitemapLink :data="subSection" />
        </h3>
        <div v-if="subSection.key && data[subSection.key] && data[subSection.key].length" class="row">
          <div v-for="(entity, idx3) in data[subSection.key]" :key="idx3" class="col col-4">
            <SitemapLink :data="entity" :parent-slug="subSection.slug" />
          </div>
        </div>
        <div v-else-if="subSection.data && subSection.data.length" class="row">
          <div v-for="(entity, idx3) in subSection.data" :key="idx3" class="col col-4">
            <SitemapLink :data="entity" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import global from '@itbpbg/lms-components/src/mixins/global';
import SitemapLink from '../components/SitemapLink';

export default {
  components: { SitemapLink },
  mixins: [global],
  async asyncData({ $axios, $config }) {
    const { page, data } = await $axios.$get('/pages/data/sitemap').catch(() => ({}));

    return {
      page,
      data,
      sections: [
        {
          name: 'Learn',
          subSections: [
            { name: 'Courses', slug: '/courses/', key: 'courses' },
            { name: 'Career Tracks', slug: '/career-tracks/', key: 'tracks' }, // Extended track names in API query
            {
              name: 'Certificates',
              data: [
                { name: 'Career Track Certificate', slug: '/career-track-certificate/' },
                { name: 'Course Certificate', slug: ($config.platform === 'ds' ? '/course-certificate/' : '/certificate/') },
                { name: 'Verify Certificate', slug: '/verify-certificate/' },
              ],
            },
            {
              ...($config.platform === 'ds'
                ? {
                  name: 'Business',
                  data: [{ name: 'Team Plan', slug: '/business/' }, { name: 'Live Training', slug: '/live-trainings/' }],
                } : {}),
            },
            {
              name: 'About the Program',
              data: [
                { name: 'Upcoming Courses', slug: '/upcoming-courses/' },
                { name: 'Pricing', slug: '/pricing/' },
                { name: 'About Us', slug: '/about-us/' },
                { name: 'Q&A Hub', slug: '/qa-hub/' },
                { name: 'Help Center', slug: '/support/' },
                { name: 'Contact Us', slug: '/contact-us/' },
              ],
            },
            {
              ...($config.platform === 'ds'
                ? {
                  name: 'Success Stories',
                  slug: '/success-stories/',
                  key: 'successStories',
                } : {}),
            },
            {
              ...($config.platform === 'ds'
                ? {
                  name: 'Instructors',
                  slug: '/instructors/',
                  key: 'instructors',
                } : {}),
            },
          ],
        },
        {
          name: 'Resources',
          slug: `${$config.platform === 'fa' ? 'https://365financialanalyst.com' : ''}/resources-center/`,
          subSections: [
            ...($config.platform === 'ds'
              ? [
                {
                  name: 'Course Notes',
                  slug: '/resources-center/course-notes/',
                  data: [
                    { name: 'Data Strategy', slug: '/resources-center/course-notes/data-strategy/' },
                    { name: 'Machine Learning with Naïve Bayes', slug: '/resources-center/course-notes/machine-learning-with-naive-bayes/' },
                    { name: 'Intro to Data and Data Science', slug: '/resources-center/course-notes/intro-to-data-and-data-science/' },
                    { name: 'Intro to Python', slug: '/resources-center/course-notes/intro-to-python/' },
                    { name: 'Python for Finance', slug: '/resources-center/course-notes/python-for-finance/' },
                    { name: 'Machine Learning with Decision Trees and Random Forests', slug: '/resources-center/course-notes/machine-learning-with-decision-trees-and-random-forests/' },
                    { name: 'Machine Learning with K-Nearest Neighbors', slug: '/resources-center/course-notes/machine-learning-with-k-nearest-neighbors/' },
                    { name: 'Statistics', slug: '/resources-center/course-notes/statistics/' },
                    { name: 'Starting a Career in Data Science', slug: '/resources-center/course-notes/starting-a-career-in-data-science/' },
                  ],
                },
                {
                  name: 'Templates',
                  slug: '/resources-center/templates/',
                  data: [
                    { name: 'Linear Regression with Statsmodels in Python', slug: '/resources-center/templates/linear-regression-with-statsmodels-in-python-1/' },
                    { name: 'Box and Whiskers Chart with ggplot2 in R', slug: '/resources-center/templates/box-and-whiskers-chart-with-ggplot2-in-r/' },
                    { name: 'Pie Chart in Excel', slug: '/resources-center/templates/pie-chart-in-excel/' },
                    { name: 'Confusion Matrix with Statsmodels in Python', slug: '/resources-center/templates/confusion-matrix-with-statsmodels-in-python/' },
                    { name: 'Feature Selection Through p-values with sklearn in Python', slug: '/resources-center/templates/feature-selection-through-p-values-with-sklearn-in-python/' },
                    { name: 'Line Chart in Excel', slug: '/resources-center/templates/line-chart-in-excel-1/' },
                    { name: 'The Enumerate Function in Python', slug: '/resources-center/templates/the-enumerate-function-in-python/' },
                    { name: 'Regression Summary Table with sklearn in Python', slug: '/resources-center/templates/regression-summary-table-with-sklearn-in-python/' },
                    { name: 'Feature Selection Through Standardization with sklearn in Python', slug: '/resources-center/templates/feature-selection-through-standardization-with-sklearn-in-python/' },
                  ],
                },
                {
                  name: 'Infographics',
                  slug: '/resources-center/infographics/',
                  data: [
                    { name: 'How to Become a Data Analyst Intern', slug: '/resources-center/infographics/how-to-become-a-data-analyst-intern/' },
                    { name: 'How to Become a Business Analyst', slug: '/resources-center/infographics/how-to-become-a-business-analyst/' },
                    { name: 'How to Become a Research Analyst', slug: '/resources-center/infographics/how-to-become-a-research-analyst/' },
                    { name: 'The 365 Data Science All In One Infographic', slug: '/resources-center/infographics/the-365-all-in-one-infographic/' },
                    { name: 'World of Supervised Machine Learning', slug: '/resources-center/infographics/world-of-supervised-machine-learning/' },
                    { name: 'How to Become a Marketing Analyst', slug: '/resources-center/infographics/how-to-become-a-marketing-analyst/' },
                  ],
                },
                {
                  name: 'Career Guides',
                  slug: '/resources-center/career-guides/',
                  data: [
                    { name: 'The Ultimate Data Science Career Guide', slug: '/resources-center/career-guides/the-ultimate-data-science-career-guide/' },
                  ],
                },
                {
                  name: 'Practice Exams',
                  slug: '/resources-center/practice-exams/',
                  data: [
                    { name: 'Excel Functions', slug: '/resources-center/practice-exams/excel-functions/' },
                    { name: 'A/B Testing for Social Media', slug: '/resources-center/practice-exams/a-b-testing-for-social-media/' },
                    { name: 'Introduction to Data Science Disciplines', slug: '/resources-center/practice-exams/introduction-to-data-science-disciplines/' },
                    { name: 'Advanced SQL', slug: '/resources-center/practice-exams/advanced-sql/' },
                    { name: 'Intrо to Machine Learning', slug: '/resources-center/practice-exams/intro-to-machine-learning/' },
                    { name: 'NumPy Fundamentals', slug: '/resources-center/practice-exams/numpy-fundamentals/' },
                    { name: 'Strings & Conditionals in Python', slug: '/resources-center/practice-exams/strings-conditionals-in-python/' },
                    { name: 'Fundamentals of Descriptive Statistics', slug: '/resources-center/practice-exams/fundamentals-of-descriptive-statistics/' },
                    { name: 'Excel Basics', slug: '/resources-center/practice-exams/excel-basics/' },
                  ],
                },
              ] : [
                {
                  name: 'Templates and Models',
                  slug: 'https://365financialanalyst.com/category/templates-and-models/',
                  data: [
                    { name: '3-Statement Model', slug: 'https://365financialanalyst.com/templates-and-models/3-statement-model-excel-template/' },
                    { name: 'Cash Flow', slug: 'https://365financialanalyst.com/templates-and-models/cash-flow-excel-template/' },
                    { name: 'Statement of Financial Position', slug: 'https://365financialanalyst.com/templates-and-models/statement-of-financial-position-excel-template/' },
                    { name: 'Net Present Value (NPV)', slug: 'https://365financialanalyst.com/templates-and-models/net-present-value-npv-excel-template/' },
                    { name: 'Monte Carlo Simulation', slug: 'https://365financialanalyst.com/templates-and-models/monte-carlo-simulation-excel-template/' },
                    { name: 'Profit & Loss (P&L)', slug: 'https://365financialanalyst.com/templates-and-models/profit-loss-pl-excel-template/' },
                    { name: 'Internal Rate of Return (IRR)', slug: 'https://365financialanalyst.com/templates-and-models/internal-rate-of-return-irr-excel-template/' },
                    { name: 'Loan Repayment Schedule', slug: 'https://365financialanalyst.com/templates-and-models/loan-repayment-schedule-excel-template/' },
                    { name: 'Balance Sheet – Excel Template', slug: 'https://365financialanalyst.com/templates-and-models/balance-sheet-excel-template/' },
                    { name: 'Lease Accounting – IFRS 16', slug: 'https://365financialanalyst.com/templates-and-models/lease-accounting-ifrs-16-excel-template/' },
                    { name: 'Time-Weighted Returns', slug: 'https://365financialanalyst.com/templates-and-models/time-weighted-returns-excel-template/' },
                    { name: 'Weighted Average Cost of Capital (WACC)', slug: 'https://365financialanalyst.com/templates-and-models/weighted-average-cost-of-capital-wacc-excel-template/' },
                    { name: 'Quantitative Methods • Formulas CFA® Level 1', slug: 'https://365financialanalyst.com/templates-and-models/quantitative-methods-formulas-cfa-level-1/' },
                    { name: 'Corporate Finance • Formulas CFA® Level 1', slug: 'https://365financialanalyst.com/templates-and-models/corporate-finance-formulas-cfa-level-1/' },
                    { name: 'Comparative Analysis – Excel Template', slug: 'https://365financialanalyst.com/templates-and-models/comparative-analysis-excel-template/' },
                  ],
                },
                {
                  name: 'Finance Data',
                  slug: 'https://365financialanalyst.com/category/data/',
                  data: [
                    { name: 'S&P 500 Stock Data', slug: 'https://365financialanalyst.com/data/sp500/' },
                    { name: 'Commodities Market Data', slug: 'https://365financialanalyst.com/data/commodities/' },
                    { name: 'Cryptocurrencies Market Data', slug: 'https://365financialanalyst.com/data/crypto/' },
                  ],
                },
                {
                  name: 'Infographics',
                  slug: 'https://365financialanalyst.com/category/infographics/',
                  data: [
                    { name: 'Who Does What in Finance ', slug: 'https://365financialanalyst.com/infographics/who-does-what-in-finance/' },
                  ],
                },
                {
                  name: 'Practice Exams',
                  slug: 'https://365financialanalyst.com/category/practice/',
                  data: [
                    { name: 'Excel Functions', slug: 'https://365financialanalyst.com/practice/excel-functions/' },
                  ],
                },
                {
                  name: 'Knowledge Hub',
                  slug: 'https://365financialanalyst.com/category/knowledge-hub/',
                  data: [
                    { name: 'Porter’s 5 Forces Analysis of Walmart – A Practical Example', slug: 'https://365financialanalyst.com/knowledge-hub/business-analysis-and-strategy/porters-5-forces-analysis-of-walmart-a-practical-example/' },
                    { name: 'Preparing the Master Budget: Step-by-Step Guide', slug: 'https://365financialanalyst.com/knowledge-hub/accounting/the-master-budget-step-by-step-guide/' },
                    { name: 'How to Calculate GDP Using the Expenditure Approach', slug: 'https://365financialanalyst.com/knowledge-hub/economics/how-to-calculate-gdp-using-the-expenditure-approach/' },
                    { name: 'Debits and Credits Cheat Sheet', slug: 'https://365financialanalyst.com/knowledge-hub/accounting/debits-and-credits-cheat-sheet/' },
                    { name: 'DuPont Analysis – A Pyramid of Ratiosramid-of-ratios', slug: 'https://365financialanalyst.com/knowledge-hub/financial-analysis/dupont-analysis-a-pyramid-of-ratios/' },
                    { name: 'What is OPEX and How Does It Impact EBITDA?', slug: 'https://365financialanalyst.com/knowledge-hub/accounting/what-is-opex-and-how-does-it-impact-ebitda/' },
                    { name: 'VLOOKUP and HLOOKUP – Practical Examples', slug: 'https://365financialanalyst.com/knowledge-hub/microsoft-office-skills/vlookup-and-hlookup-practical-examples/' },
                    { name: 'What Is Industry Concentration?', slug: 'https://365financialanalyst.com/knowledge-hub/business-analysis-and-strategy/what-is-industry-concentration/' },
                    { name: 'The Industry Life Cycle Model Explained', slug: 'https://365financialanalyst.com/knowledge-hub/business-analysis-and-strategy/the-industry-life-cycle-model-explained-introduction-growth-maturity-decline/' },
                    { name: 'How to Calculate Rates of Return in Excel', slug: 'https://365financialanalyst.com/knowledge-hub/trading-and-investing/how-to-calculate-rates-of-return-in-excel/' },
                    { name: 'Key Elements of Company Analysis', slug: 'https://365financialanalyst.com/knowledge-hub/business-analysis-and-strategy/key-elements-of-a-company-analysis/' },
                    { name: 'The Startup Finance Team: 4 Key Functions', slug: 'https://365financialanalyst.com/knowledge-hub/corporate-finance/start-up-finance-team/' },
                    { name: 'Porter’s 5 Forces Model: Bargaining Power of Suppliers', slug: 'https://365financialanalyst.com/knowledge-hub/business-analysis-and-strategy/porters-5-forces-model-bargaining-power-of-suppliers/' },
                    { name: 'How to Calculate Year-on-Year Growth Percentage?', slug: 'https://365financialanalyst.com/knowledge-hub/financial-analysis/how-to-calculate-year-on-year-growth-percentage/' },
                    { name: 'What is D&A and How Is It Related to Fixed Assets?', slug: 'https://365financialanalyst.com/knowledge-hub/accounting/what-is-da-and-how-is-it-related-to-fixed-assets/' },
                    { name: 'How to Download Historical Price Data In Excel Using Yahoo Finance', slug: 'https://365financialanalyst.com/knowledge-hub/trading-and-investing/how-to-download-historical-price-data-in-excel-using-yahoo-finance/' },
                    { name: 'Expected Loss and Its Components', slug: 'https://365financialanalyst.com/knowledge-hub/credit-analysis/expected-loss-and-its-components-probability-of-default-loss-given-default-and-exposure-at-default/' },
                    { name: 'Common Stock vs. Preferred Stock', slug: 'https://365financialanalyst.com/knowledge-hub/trading-and-investing/common-stock-vs-preferred-stock/' },
                    { name: 'Cyclical vs. Non-Cyclical Industries', slug: 'https://365financialanalyst.com/knowledge-hub/business-analysis-and-strategy/cyclical-vs-non-cyclical-industries/' },
                    { name: 'What’s the Difference Between Direct and Indirect Cash Flow Methods?', slug: 'https://365financialanalyst.com/knowledge-hub/accounting/whats-the-difference-between-direct-and-indirect-cash-flow-methods/' },
                    { name: 'NPV vs. IRR', slug: 'https://365financialanalyst.com/knowledge-hub/corporate-finance/npv-vs-irr/' },
                    { name: 'EBITDAR vs. EBITDA vs. EBITA vs. EBIT vs. EBT vs. EBIAT vs. Adjusted EBITDA', slug: 'https://365financialanalyst.com/knowledge-hub/accounting/ebitdar-vs-ebitda-vs-ebita-vs-ebit-vs-ebt-vs-ebiat-vs-adjusted-ebitda/' },
                    { name: 'Budget Preparation: 6 Key Steps', slug: 'https://365financialanalyst.com/knowledge-hub/accounting/budget-preparation-6-key-steps/' },
                    { name: 'What Is the Discounted Payback Period?', slug: 'https://365financialanalyst.com/knowledge-hub/corporate-finance/what-is-the-discounted-payback-period/' },
                    { name: 'Porter’s 5 Forces Model: Bargaining Power of Buyers', slug: 'https://365financialanalyst.com/knowledge-hub/business-analysis-and-strategy/porters-5-forces-model-bargaining-power-of-buyers/' },
                    { name: 'What is a Common-Size Balance Sheet?', slug: 'https://365financialanalyst.com/knowledge-hub/accounting/what-is-a-common-size-balance-sheet/' },
                    { name: 'What Are Forward Contracts, Futures Contracts, and Swaps?', slug: 'https://365financialanalyst.com/knowledge-hub/trading-and-investing/what-are-forward-contracts-futures-contracts-and-swaps/' },
                    { name: 'What is a Common-Size Cash Flow Statement?', slug: 'https://365financialanalyst.com/knowledge-hub/accounting/what-is-a-common-size-cash-flow-statement/' },
                    { name: 'Porter’s 5 Forces Model: Threat of New Entrants', slug: 'https://365financialanalyst.com/knowledge-hub/business-analysis-and-strategy/threat-of-new-entrants/' },
                    { name: 'Valuation Ratios', slug: 'https://365financialanalyst.com/knowledge-hub/financial-analysis/valuation-ratios/' },
                    { name: 'How to Calculate GDP Using the Income Approach', slug: 'https://365financialanalyst.com/knowledge-hub/economics/how-to-calculate-gdp-using-the-income-approach/' },
                    { name: 'Elements in a Thorough Industry Analysis', slug: 'https://365financialanalyst.com/knowledge-hub/business-analysis-and-strategy/elements-in-a-thorough-industry-analysis/' },
                    { name: 'Income Statement Items Explained (With Examples)', slug: 'https://365financialanalyst.com/knowledge-hub/accounting/income-statement-items-explained-with-examples/' },
                    { name: 'Peer Group Analysis', slug: 'https://365financialanalyst.com/knowledge-hub/business-analysis-and-strategy/peer-group-analysis/' },
                    { name: 'The Different Types of Investment Banking Strategies', slug: 'https://365financialanalyst.com/knowledge-hub/m-and-a-deals-and-transactions/the-different-types-of-investment-banking-strategies/' },
                    { name: 'Audit Reports', slug: 'https://365financialanalyst.com/knowledge-hub/accounting/audit-reports/' },
                  ],
                },
                {
                  name: 'Career Advice',
                  slug: 'https://365financialanalyst.com/category/career-advice/',
                  data: [
                    { name: 'Can You Become a Financial Analyst?', slug: 'https://365financialanalyst.com/career-advice/become-a-financial-analyst/' },
                    { name: 'Answering Behavioral Interview Questions', slug: 'https://365financialanalyst.com/career-advice/answering-tips-for-behavioral-interview-questions/' },
                    { name: 'Answering Qualification Interview Questions', slug: 'https://365financialanalyst.com/career-advice/answering-tips-for-qualification-interview-questions/' },
                    { name: 'Answering Difficult Interview Questions', slug: 'https://365financialanalyst.com/career-advice/answering-tips-for-difficult-interview-questions/' },
                    { name: 'Answering Guesstimate Interview Questions', slug: 'https://365financialanalyst.com/career-advice/answering-tips-for-guesstimate-interview-questions/' },
                    { name: 'Answering Brainteaser Interview Questions', slug: 'https://365financialanalyst.com/career-advice/answering-tips-for-brainteaser-interview-questions/' },
                  ],
                },
              ]
            )],
        },
        ($config.platform === 'ds' && {
          name: 'Blog',
          slug: '/blog/',
          subSections: [
            {
              name: 'Career Advice',
              slug: '/career-advice/',
              data: [
                { name: 'Data Architect Interview Questions And Answers', slug: '/career-advice/job-interview-tips/data-architect-interview-questions/' },
                { name: 'How to Transition Your Career into Data Science: A Step-by-Step Guide', slug: '/career-advice/transition-into-data-science-career/' },
                { name: '18 Data Science Consulting Companies Hiring Now', slug: '/career-advice/career-guides/data-science-consulting-companies/' },
                { name: 'BI Analyst Interview Questions And Answers', slug: '/career-advice/job-interview-tips/bi-analyst-interview-questions/' },
                { name: 'Guide To The Best Data Science Bachelor’s Degrees', slug: '/career-advice/career-guides/best-degrees-data-scientist/' },
                { name: 'Defining Data Science: The What, Where and How of Data Science', slug: '/career-advice/career-guides/defining-data-science/' },
                { name: 'Data Analyst Interview Questions and Answers', slug: '/career-advice/job-interview-tips/data-analyst-interview-questions/' },
                { name: 'Starting a Career in Data Science: The Ultimate Guide', slug: '/career-advice/career-guides/career-data-science-ultimate-guide/' },
                { name: 'SQL Interview Questions', slug: '/career-advice/job-interview-tips/sql-interview-questions/' },
                { name: 'How to Get Into Data Science With an Economics Degree?', slug: '/career-advice/career-guides/transition-data-science-economics/' },
                { name: 'Top 10 Machine Learning Interview Questions and Answers', slug: '/career-advice/job-interview-tips/machine-learning-interview-questions-and-answers/' },
                { name: 'Data Scientist Resume Sample and Template', slug: '/career-advice/job-interview-tips/data-scientist-resume-sample/' },
                { name: 'Data Engineer Interview Questions And Answers', slug: '/career-advice/job-interview-tips/data-engineer-interview-questions/' },
                { name: 'How to Become a Data Scientist with No Experience?', slug: '/career-advice/how-to-become-a-data-scientist-with-no-experience/' },
                { name: 'How to Find a Data Science Job if You Don’t Have a Data Science Degree?', slug: '/career-advice/career-guides/how-to-find-data-science-job-without-degree/' },
              ],
            },
            {
              name: 'Tutorials',
              slug: '/tutorials/',
              data: [
                { name: 'Sum of Squares Total, Sum of Squares Regression and Sum of Squares Error', slug: '/tutorials/statistics-tutorials/sum-squares/' },
                { name: '10 Excel Project Ideas for Your Data Science Portfolio', slug: '/tutorials/excel-tutorials/10-excel-project-ideas-for-your-data-science-portfolio/' },
                { name: 'Examples of Numerical and Categorical Variables', slug: '/tutorials/statistics-tutorials/numerical-categorical-data/' },
                { name: 'How to Build a Predictive Model in Python?', slug: '/tutorials/python-tutorials/predictive-model-python/' },
                { name: 'How To Perform A Linear Regression In Python (With Examples!)', slug: '/tutorials/python-tutorials/linear-regression/' },
                { name: 'Hypothesis Testing with Z-Test: Significance Level and Rejection Region', slug: '/tutorials/statistics-tutorials/significance-level-reject-region/' },
                { name: 'How to Combine PCA and K-means Clustering in Python?', slug: '/tutorials/python-tutorials/pca-k-means/' },
                { name: 'What is a Distribution in Statistics?', slug: '/tutorials/statistics-tutorials/distribution-in-statistics/' },
                { name: 'Best Free Public Datasets to Use in Python', slug: '/tutorials/python-tutorials/free-public-datasets-python/' },
                { name: 'Database vs Spreadsheet: What’s the Difference?', slug: '/tutorials/sql-tutorials/database-vs-spreadsheet/' },
                { name: 'How to Create a Matplotlib Bar Chart in Python?', slug: '/tutorials/python-tutorials/bar-chart-python-matplotlib/' },
                { name: 'Measuring Explanatory Power with the R-squared', slug: '/tutorials/statistics-tutorials/r-squared/' },
                { name: 'Hypothesis Testing: Null Hypothesis and Alternative Hypothesis', slug: '/tutorials/statistics-tutorials/null-hypothesis/' },
                { name: 'Exploring the 5 OLS Assumptions for Linear Regression Analysis', slug: '/tutorials/statistics-tutorials/ols-assumptions/' },
                { name: 'Measures of Variability: Coefficient of Variation, Variance, and Standard Deviation', slug: '/tutorials/statistics-tutorials/coefficient-variation-variance-standard-deviation/' },
              ],
            },
            {
              name: 'News',
              slug: '/news/',
              data: [
                { name: 'The 365 Learning Data Challenge', slug: '/news/365-learning-data-challenge/' },
                { name: 'Free Month! Get Full Access to All Courses on Our Newly Redesigned Platform', slug: '/news/free-month/' },
                { name: '#21DaysFREE for all courses on the 365 Data Science platform', slug: '/news/365-data-science-all-courses-on-platform-free/' },
                { name: '365 Card Collections', slug: '/news/365-card-collections/' },
                { name: 'Add Fun to Data Science Learning', slug: '/news/add-fun-to-data-science-learning/' },
                { name: 'New Course! Machine Learning in Excel', slug: '/news/new-course-machine-learning-in-excel/' },
                { name: 'Free Access to All Data Science Courses!', slug: '/news/free-access-covid-19/' },
                { name: 'New Course! A/B Testing in Python', slug: '/news/new-course-ab-testing-in-python/' },
                { name: 'New Course! Machine Learning with Support Vector Machines', slug: '/news/new-course-machine-learning-with-support-vector-machines/' },
              ],
            },
            {
              name: 'Trending Topics',
              slug: '/trending/',
              data: [
                { name: 'Who Was Affected by the 2022–2023 Tech Layoffs? A Comprehensive Study by 365 Data Science', slug: '/trending/who-was-affected-by-the-2022-2023-tech-layoffs/' },
                { name: 'What Is a Data Cube?', slug: '/trending/data-cube/' },
                { name: 'The History of Data: From Ancient Times to Modern Day', slug: '/trending/history-of-data/' },
                { name: 'Top 10 Data Visualization Project Ideas 2021', slug: '/trending/data-visualization-project-ideas/' },
                { name: 'Correlation Analysis: All the Basics You Need to Know', slug: '/trending/correlation-analysis/' },
                { name: 'Trend Analysis: What Is It and Why Is It Important?', slug: '/trending/trend-analysis/' },
                { name: 'Data Visualization: How to Choose the Right Chart and Graph for Your Data', slug: '/trending/chart-types-and-how-to-select-the-right-one/' },
                { name: 'Top 10 Free Dataset Resources for Data Science Projects', slug: '/trending/free-dataset-resources/' },
                { name: 'ChatGPT: How to Understand and Compete with the AI Bot', slug: '/trending/chatgpt-how-to-understand-and-compete-with-the-ai-bot/' },
              ],
            },
            {
              name: 'Meet the 365 Data Science Team',
              slug: '/meet-the-team/',
              data: [
                { name: 'Interview with Ned Krastev, Co-founder of 365 Data Science', slug: '/meet-the-team/interview-ned-krastev/' },
                { name: 'Interview with Martin Ganchev, Instructor at 365 Data Science', slug: '/meet-the-team/interview-martin-ganchev/' },
                { name: 'Interview with Nikola Pulev, Instructor at 365 Data Science', slug: '/meet-the-team/interview-nikola-pulev/' },
                { name: 'Interview with Martina Videnova, Graphic Designer at 365 Data Science', slug: '/meet-the-team/interview-martina-videnova/' },
                { name: 'Interview with Elitsa Kaloyanova, Instructor at 365 Data Science', slug: '/meet-the-team/interview-elitsa-kaloyanova/' },
              ],
            },
          ],
        }),
      ].filter((v) => v), // remove empty
    };
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
    ]);

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps(this.page),
      script: [
        {
          vmid: 'jsonld',
          type: 'application/ld+json',
          json: [linkedData],
        },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
.sitemap {
  padding-top: 200px;
  padding-bottom: 120px;

  .learn {
    margin: var(--spacing-80) 0 var(--spacing-40);
  }

  .title-link {
    ::v-deep .lnk {
      color: var(--main-dark-shade-0);
      text-decoration: underline;
    }
  }

  .section {
    margin-top: var(--spacing-32);

    .col {
      margin-bottom: var(--spacing-32);

      ::v-deep .lnk {
        color: var(--accent-shade-1);
        font-weight: 600;
        line-height: 1.5;
      }
    }
  }
}

.fa .sitemap .section .col ::v-deep .lnk {
  color: var(--accent);
}
</style>
